import React, { lazy } from 'react';
import { Task } from '../../types';
import { useProject } from '../../contexts/ProjectContext';
import { useUI } from '../../contexts/UIContext';
import { AppViewRouterProps, EMPTY_METADATA } from './types';

const GanttView = lazy(() => import('../GanttView'));
const KanbanView = lazy(() => import('../KanbanView'));
const ListView = lazy(() => import('../ListView'));
const MindMapView = lazy(() => import('../MindMapView'));

export const ProjectCoreViewsRouter: React.FC<AppViewRouterProps> = (props) => {
  const {
    isProjectEditor,
    deferredVisibleTasks,
    deferredTasks,
    handleAddTask,
    todayStr,
    containerHeight,
    droppedZoneId,
    activeDragId,
    registerViewDragHandlers,
    handleReorderTask,
    ganttState,
  } = props;

  const projectCtx = useProject();
  const {
    activeProject,
    currentProjectId,
    projectName,
    updateTask,
    updateTasksMultiple,
    deleteTask,
    projectMetadata,
    updateProjectMetadata,
    setProjectMetadata,
    allProjects,
  } = projectCtx;

  const uiCtx = useUI();
  const {
    viewMode,
    searchQuery,
    searchMode,
    searchMatchIds,
    searchResults,
    searchIndex,
    activeSearchMatchId,
    kanbanMode,
    setKanbanMode,
    showArchived,
    showCompleted,
    setEditingTask,
  } = uiCtx;

  const dayWidth = props.dayWidth ?? ganttState?.dayWidth ?? 28;
  const headerDates = props.headerDates ?? ganttState?.headerDates ?? [];
  const months = props.months ?? ganttState?.months ?? [];
  const dateToIndexMap = props.dateToIndexMap ?? ganttState?.dateToIndexMap ?? {};
  const activeDrag = props.activeDrag ?? ganttState?.activeDrag;
  const dragOffset = props.dragOffset ?? ganttState?.dragOffset ?? 0;
  const handleGanttDragStart = props.handleGanttDragStart ?? ganttState?.handleDragStart;
  const handleLinkStart = props.handleLinkStart ?? ganttState?.handleLinkStart;
  const gridStart = props.gridStart ?? ganttState?.gridStart;
  const getDependencyPath = props.getDependencyPath ?? ganttState?.getDependencyPath;
  const fullTaskMap = props.fullTaskMap ?? ganttState?.fullTaskMap;
  const linkDrag = props.linkDrag ?? ganttState?.linkDrag;
  const linkTargetId = props.linkTargetId ?? ganttState?.linkTargetId;
  const handleGridMouseDown = props.handleGridMouseDown ?? ganttState?.handleGridMouseDown;
  const handleWheel = props.handleWheel ?? ganttState?.handleWheel;
  const scrollContainerRef = props.scrollContainerRef ?? ganttState?.scrollContainerRef;
  const sidebarListRef = props.sidebarListRef ?? ganttState?.sidebarListRef;
  const ganttListRef = props.ganttListRef ?? ganttState?.ganttListRef;
  const handleGanttScroll = props.handleGanttScroll ?? ganttState?.handleGanttScroll;
  const handleSidebarScroll = props.handleSidebarScroll ?? ganttState?.handleSidebarScroll;
  const listScrollTop = props.listScrollTop ?? ganttState?.listScrollTop ?? 0;
  const taskIdMap = props.taskIdMap ?? ganttState?.taskIdMap;
  const scrollToTask = props.scrollToTask ?? ganttState?.scrollToTask;
  const selectedTaskId = props.selectedTaskId ?? ganttState?.selectedTaskId;
  const scrollToToday = props.scrollToToday ?? ganttState?.scrollToToday;
  const scrollToInitialView = props.scrollToInitialView ?? ganttState?.scrollToInitialView;
  const onContainerRef = props.onContainerRef ?? ganttState?.onContainerRef;

  // Phase 3 · Schritt 3.4: Keep high-frequency hover states local to GanttView unless explicitly controlled via props
  const hoveredTaskId = props.hoveredTaskId;
  const setHoveredTaskId = props.setHoveredTaskId;
  const hoveredDayIndex = props.hoveredDayIndex;
  const setHoveredDayIndex = props.setHoveredDayIndex;
  const showDependencies = props.showDependencies ?? uiCtx.showDependencies;
  const handleTaskHover = props.handleTaskHover ?? uiCtx.handleTaskHover;
  const handleBrainstorm = props.handleBrainstorm ?? uiCtx.handleBrainstorm;

  const exportKanbanColumnAsExcel = props.exportKanbanColumnAsExcel ?? projectCtx.exportKanbanColumnAsExcel;
  const exportTaskTableAsExcel = props.exportTaskTableAsExcel ?? projectCtx.exportTaskTableAsExcel;
  const upgradeIdeaToTask = props.upgradeIdeaToTask ?? projectCtx.upgradeIdeaToTask;
  const batchUpdateTaskPositions = props.batchUpdateTaskPositions ?? projectCtx.batchUpdateTaskPositions;

  return (
    <>
      {/* Gantt View */}
      {viewMode === 'gantt' && (
        <div data-tour="gantt-interactive-area" className="flex-1 flex flex-col min-h-0">
          <GanttView 
            key={currentProjectId}
            tasks={deferredVisibleTasks}
            allTasks={deferredTasks}
            isReadOnly={!isProjectEditor}
            zoom={dayWidth / 28}
            scrollLeft={0}
            timelineDates={headerDates}
            months={months}
            dateToIndexMap={dateToIndexMap}
            dayWidth={dayWidth}
            onTaskUpdate={updateTask}
            onTaskDelete={deleteTask}
            onTaskSelect={setEditingTask}
            onTaskHover={handleTaskHover}
            showDependencies={showDependencies}
            isDragging={!!activeDrag}
            activeDrag={activeDrag}
            dragOffset={dragOffset}
            onDragStart={handleGanttDragStart}
            onLinkStart={handleLinkStart}
            gridStart={gridStart}
            todayPos={dateToIndexMap[todayStr] ?? -1}
            hoveredTaskId={hoveredTaskId}
            setHoveredTaskId={setHoveredTaskId}
            getDependencyPath={getDependencyPath}
            fullTaskMap={fullTaskMap}
            linkDrag={linkDrag}
            linkTargetId={linkTargetId}
            onGridMouseDown={handleGridMouseDown}
            onWheel={handleWheel}
            searchResults={searchResults}
            searchIndex={searchIndex}
            searchMatchIds={searchMatchIds}
            activeSearchMatchId={activeSearchMatchId}
            searchMode={searchMode}
            searchQuery={searchQuery}
            scrollContainerRef={scrollContainerRef}
            sidebarListRef={sidebarListRef}
            ganttListRef={ganttListRef}
            handleGanttScroll={handleGanttScroll}
            handleSidebarScroll={handleSidebarScroll}
            listScrollTop={listScrollTop}
            taskIdMap={taskIdMap}
            containerHeight={containerHeight}
            hoveredDayIndex={hoveredDayIndex}
            setHoveredDayIndex={setHoveredDayIndex}
            scrollToTask={scrollToTask}
            selectedTaskId={selectedTaskId}
            scrollToToday={scrollToToday}
            scrollToInitialView={scrollToInitialView}
            onAddTask={handleAddTask}
            onContainerRef={onContainerRef}
          />
        </div>
      )}

      {/* Kanban View */}
      {viewMode === 'kanban' && activeProject && (
        <div className="flex-1 flex flex-col min-h-0">
          <KanbanView 
            isActive={true}
            showArchived={showArchived}
            showCompleted={showCompleted}
            isReadOnly={!isProjectEditor}
            project={{
              ...activeProject,
              tasks: deferredVisibleTasks
            }}
            mode={kanbanMode}
            onModeChange={setKanbanMode}
            onTaskUpdate={updateTask}
            onTaskUpdateMultiple={updateTasksMultiple}
            onTaskDelete={deleteTask}
            onAddTask={handleAddTask}
            onBrainstorm={(task: Task) => handleBrainstorm(task.name)}
            metadata={projectMetadata}
            onUpdateMetadata={(updates) => updateProjectMetadata(currentProjectId, updates)}
            globalMetadata={allProjects.find(p => p.id === 'inbox_inforum')?.metadata || EMPTY_METADATA}
            onUpdateGlobalMetadata={(updates) => updateProjectMetadata('inbox_inforum', updates)}
            todayStr={todayStr}
            onTaskSelect={(task) => setEditingTask({ ...task, projectId: task.projectId || currentProjectId })}
            onTaskReorder={handleReorderTask}
            exportKanbanColumnAsExcel={exportKanbanColumnAsExcel}
            searchQuery={searchQuery}
            searchMode={searchMode}
            searchMatchIds={searchMatchIds}
            searchResults={searchResults}
            searchIndex={searchIndex}
            activeSearchMatchId={activeSearchMatchId}
            activeDragId={activeDragId}
            onRegisterDragHandlers={registerViewDragHandlers}
            droppedZoneId={droppedZoneId}
          />
        </div>
      )}

      {/* Table / List View */}
      {viewMode === 'list' && (
        <div className="flex-1 flex flex-col min-h-0">
          <ListView 
            tasks={deferredVisibleTasks}
            allTasks={deferredTasks}
            isReadOnly={!isProjectEditor}
            onTaskUpdate={updateTask}
            onTaskDelete={deleteTask}
            onAddTask={(type, parentId) => {
              const newTask = handleAddTask(type, parentId, currentProjectId);
              if (newTask) setEditingTask({ ...newTask, projectId: currentProjectId });
            }}
            onTaskClick={(task) => setEditingTask({ ...task, projectId: task.projectId || currentProjectId })}
            activeSearchMatchId={activeSearchMatchId}
            searchQuery={searchQuery}
            searchMatchIds={searchMatchIds}
            searchMode={searchMode}
            exportTaskTableAsExcel={exportTaskTableAsExcel}
          />
        </div>
      )}

      {/* MindMap View */}
      {viewMode === 'mindmap' && (
        <div className="flex-1 flex flex-col min-h-0">
          <MindMapView 
            projectName={projectName}
            projectId={currentProjectId}
            isActive={true}
            tasks={deferredVisibleTasks}
            isReadOnly={!isProjectEditor}
            onTaskUpdate={updateTask}
            upgradeIdeaToTask={upgradeIdeaToTask}
            onAddTask={handleAddTask}
            onTaskClick={(task) => setEditingTask({ ...task, projectId: task.projectId || currentProjectId })}
            onTaskSelect={(task) => setEditingTask({ ...task, projectId: task.projectId || currentProjectId })}
            onDelete={deleteTask}
            batchUpdateTaskPositions={batchUpdateTaskPositions}
            metadata={projectMetadata}
            onMetadataUpdate={setProjectMetadata}
            searchQuery={searchQuery}
            searchMatchIds={searchMatchIds}
            activeSearchMatchId={activeSearchMatchId}
            searchMode={searchMode}
          />
        </div>
      )}
    </>
  );
};

export default ProjectCoreViewsRouter;
